import React, { useCallback , useEffect, useState } from 'react';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import './DownloadPageComponent.css'
import { dateFilter, downloadReportAttributes, downloadReportRequestBody, reportListObject } from '../../types/types';
import { downloadReport, downloadReportOfID, getReportFilterAttribute, task } from '../../../../services/reportService';
import { downloadLiveFilterReport, downloadNewReport, LiveReportDownloadRequest, NewReportRequestBody } from '../../../../services/newReportService';
import { GenericPopUp } from '../../../../components/popup/genericPopUp';
import { popupType } from "../../../../types/types";
import { EnvironmentRegistry } from '../../../../services/registry/environmentRegistry';
import { Loader } from '../../../../components/loader/Loader';
import { useReportGlobalFilters } from '../../../../providers/ReportGlobalFiltersProvider';
import { TranslationEnum, usePortalTranslation } from '../../../../providers/TranslationProvider';
import { useDispatch,useSelector } from 'react-redux';
import { addDownload,setDownloadCount,setShowDownload ,removeCancelDownload,updateDownload,removeDownload,showCancelPopup} from '../../../../stateManagement/actions/downloadBarActions';
import store from '../../../../stateManagement/store';
import { R } from 'ace-builds-internal/lib/bidiutil';
import { getMetaDataConfig } from '../../../../services/uiConfigService';
import { configurationAttributeType } from '../../../../types';
import {getLob} from '../../../../services/authenticationService';

type AttributeFilterPayload = {
  filters?: Array<{ key: string; value: string | string[] }>;
};

const appendValueToFiltersMap = (
  map: Record<string, string[]>,
  key: string,
  value?: string | string[]
) => {
  if (!key || value === undefined || value === null) {
    return;
  }

  const valuesArray = Array.isArray(value) ? value : [value];
  const sanitizedValues = valuesArray
    .filter((item) => item !== undefined && item !== null && `${item}`.trim().length > 0)
    .map((item) => {
      const trimmed = `${item}`.trim();
      const utc = convertToUtcString(trimmed);
      return utc ?? trimmed;
    });

  if (!sanitizedValues.length) {
    return;
  }

  const existing = map[key] ?? [];
  const merged = new Set([...existing, ...sanitizedValues]);
  map[key] = Array.from(merged);
};

const buildFiltersMapFromAttributes = (attributes: downloadReportAttributes): Record<string, string[]> => {
  const map: Record<string, string[]> = {};
  const attributeFilter = attributes.filter as AttributeFilterPayload | undefined;

  if (attributeFilter?.filters?.length) {
    attributeFilter.filters.forEach(({ key, value }) => appendValueToFiltersMap(map, key, value));
  }

  appendValueToFiltersMap(map, 'period', attributes.period);
  appendValueToFiltersMap(map, 'year', attributes.year);
  appendValueToFiltersMap(map, 'type', attributes.type);

  return map;
};

const convertToUtcString = (dateTime?: string) => {
  if (!dateTime) {
    return undefined;
  }

  const trimmed = dateTime.trim();
  if (!trimmed.length) {
    return undefined;
  }

  const normalizedInput = trimmed.includes('T')
    ? trimmed.replace(' ', 'T')
    : trimmed.replace(' ', 'T');

  const candidate = normalizedInput.endsWith('Z')
    ? normalizedInput
    : `${normalizedInput}`;

  const date = new Date(candidate);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
};

const extractFileNameFromHeaders = (contentDisposition?: string, fallbackFileName?: string) => {
  if (!contentDisposition) {
    return fallbackFileName;
  }

  const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|")?([^\";]+)/i);
  if (fileNameMatch?.[1]) {
    const rawFileName = fileNameMatch[1].replace(/"/g, '').trim();
    try {
      return decodeURIComponent(rawFileName);
    } catch {
      return rawFileName;
    }
  }

  return fallbackFileName;
};
const DownloadPageComponent: React.FC<reportListObject & dateFilter> = ({
  id,
  report,
  name,
  filter,
  batchDownload,
  selectedParentFilter,
  startDateForAttribute,
  endDateForAttribute,
  yearForAttribute,
  periodForAttribute,
  selectedLiveFilter,
  setShowReportModal,
  // quarterForAttribute
}) => {
  const { translate } = usePortalTranslation();
  const CUR_COMPONENT_COMMON="commonPortal";
  const CUR_COMPONENT = "Reports"
  const [openGenericModal, setOpenGenericModal] = useState<boolean>(false);
  const [popupType, setPopupType] = useState<popupType>("Alert");
  const [loader, setLoader] = useState(false);
  const dispatch=useDispatch()
  const [genericMessage, setGenericMessage] = useState<string>("");
  const [fileFormat, setFileFormat] = useState<string>(useSelector((state: any) => state.fileFormat.fileFormat));
  const downloads = useSelector(() => store.getState().downloadBarReducer.downloads);
  const [distributorCodeKey, setDistributorCodeKey] = useState<string>("distributor_code");
  const [reportFormat, setReportFormat] = useState<string | undefined>(undefined);
  const openPopup = useCallback((message: string, type: popupType) => {
    setPopupType(type);
    setGenericMessage(message);
    setOpenGenericModal(true);
  }, []);

  const { reportGlobalFiltersState } = useReportGlobalFilters()

  const addOrUpdateDownload=(id:string,name:string)=>{
    const existingDownload =downloads.find((d) => d.id === id);
    if(existingDownload){
      dispatch(updateDownload(id,[],0))
    }
    else{
      dispatch(addDownload({id,name,statusList:[],totalRecordsCount:0}))
    }
  }

   useEffect(() => {
      const fetchPortalConfig = async () => {
        const portalConfig = await getMetaDataConfig(
          "clientConfig",
          "portal_configuration"
        );
        const portal_config: configurationAttributeType[] =
          portalConfig?.domainValues ?? [];
      
        const configDistributorCodeKey = portal_config?.find(
          (item) => item.name === "enableDistributorCode"
        )?.value ?? "distributor_code"; 
        setDistributorCodeKey(configDistributorCodeKey);
        
        const configReportFormat = portal_config?.find(
          (item) => item.name === "reportFormat"
        )?.value;
        setReportFormat(configReportFormat || undefined);
      };
      fetchPortalConfig();
    }, []);

  const downloadLiveFilterReports = async (requestBodies: downloadReportRequestBody[]) => {
    const startISO = convertToUtcString(startDateForAttribute);
    const endISO = convertToUtcString(endDateForAttribute);

    if (!startISO || !endISO) {
      openPopup("Please select and apply filters", "Alert");
      setLoader(false);
      return;
    }

    if (!requestBodies.length) {
      setLoader(false);
      openPopup("No report found for download", "Alert");
      return;
    }

    addOrUpdateDownload(id, name);
    dispatch(setShowDownload(true));
    dispatch(setDownloadCount(1));
    dispatch(updateDownload(id, ["INPROGRESS"], 0));

    try {
      const authContext = JSON.parse(localStorage.authContext || '{}');
      const loggedInUser = authContext?.user?.loginId;

      for (const requestBody of requestBodies) {
        const resolvedReportName =
          requestBody.attributes.name ||
          selectedParentFilter?.reportName ||
          requestBody.attributes.reportId ||
          id;

        if (!resolvedReportName) {
          console.warn("Skipping download. Unable to resolve report name.", requestBody);
          continue;
        }

        const filtersMap = buildFiltersMapFromAttributes(requestBody.attributes);

        if (selectedParentFilter?.id) {
          appendValueToFiltersMap(filtersMap, 'type', selectedParentFilter.id);
        }

        if(loggedInUser){
        appendValueToFiltersMap(filtersMap, distributorCodeKey, distributorCodeKey === "distributor_code" ? loggedInUser : getLob());
        }

        const liveRequest: LiveReportDownloadRequest = {
          reportName: resolvedReportName,
          filters: Object.keys(filtersMap).length ? { map: filtersMap } : undefined,
          dateRange: {
            startDate: startISO,
            endDate: endISO
          },
          ...(reportFormat && { format: reportFormat })
        };

        const response = await downloadLiveFilterReport(liveRequest);

        const blob = new Blob(
          [response.data],
          { type: response.headers['content-type'] ?? 'application/octet-stream' }
        );
        const fileName = extractFileNameFromHeaders(
          response.headers['content-disposition'],
          `${resolvedReportName}.xlsx`
        );

        const downloadUrl = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = downloadUrl;
        anchor.download = fileName || `${resolvedReportName}.xlsx`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(downloadUrl);
      }

      dispatch(updateDownload(id, ["SUCCESS"], 0));
      dispatch(setDownloadCount(-1));
      dispatch(removeDownload(id));
    } catch (error) {
      console.error("Error while downloading live filter report:", error);
      dispatch(updateDownload(id, ["FAILURE"], 0));
      dispatch(setDownloadCount(-1));
      openPopup("Error downloading report", "Error");
    } finally {
      setLoader(false);
    }
  };
  const donwloadReportWithStatus = async (idList: Record<string, any>, failedIds: string[]) => {
    let latestCancelledDownloads = store.getState().downloadBarReducer.cancelledDownloads;
    if(latestCancelledDownloads.includes(id)){
      dispatch(removeCancelDownload(id));
      return;
    }
 
    let statusList: string[] = [];
    let records:number=0
    let latestDownloads=store.getState().downloadBarReducer.downloads;
    const existingDownload =latestDownloads.find((d) => d.id === id);
    for (const id of [...Object.keys(idList)]) {
      try{
      const res = await task(id);
      let data = res.data.features[0];
      if (data.status !== 'SUCCESS' && data.status !== 'INPROGRESS') {
        failedIds.push(idList[id]);
        delete idList[id];
      }
      statusList.push(data.status);
      records=res?.data?.features[0]?.attributes?.totalRecordsCount??existingDownload?.totalRecordsCount;
    }
    catch(err){
      dispatch(setDownloadCount(-1));
     dispatch(showCancelPopup(false));
     dispatch(updateDownload(id,["FAILURE"],0));
     setLoader(false);
    }
    }
    
    if (statusList.some((status) => (status === 'INPROGRESS'||status==="SUCCESS")))
    dispatch(updateDownload(id,statusList,records));
    else
    dispatch(updateDownload(id,statusList,0));
   
    if (statusList.every((status) => status === 'SUCCESS')) {
      Object.keys(idList).forEach((id, index) => {
        setTimeout(() => {
          downloadReportOfID(id,batchDownload);
        }, index * 200);
      });
      latestCancelledDownloads = store.getState().downloadBarReducer.cancelledDownloads;
      if(latestCancelledDownloads.includes(id)){
       dispatch(removeCancelDownload(id));
       return;
      }
      setLoader(false);
      dispatch(removeDownload(id));
      dispatch(setDownloadCount(-1));
      // if (failedIds.length > 0) {
      //   const reportNames = failedIds.length<=1 ? name : failedIds.join(", ") ;
      //   openPopup(`Something went wrong with ${reportNames} report`, "Error");
      // }
    } else if (statusList.some((status) => status === 'INPROGRESS')) {
      latestCancelledDownloads = store.getState().downloadBarReducer.cancelledDownloads;
      if(latestCancelledDownloads.includes(id)){
        dispatch(removeCancelDownload(id));
        return;
      }
      setTimeout(() => donwloadReportWithStatus(idList, failedIds), (1000));
    }
    else {
      latestCancelledDownloads = store.getState().downloadBarReducer.cancelledDownloads;
      if(latestCancelledDownloads.includes(id)){
        dispatch(removeCancelDownload(id));
        return;
      }
     dispatch(setDownloadCount(-1));
     dispatch(showCancelPopup(false));
     setLoader(false);
      // if (failedIds.length > 0) {
      //   const reportNames = failedIds.length<=1 ? name : failedIds.join(", ") ;
      //   openPopup(`Something went wrong with ${reportNames} report`, "Error");
      // }
    }
  };

  const callMethod = async (requestBodyList: downloadReportRequestBody[]) => {
    
    let idList: Record<string, any> = [];
    for (const requestBody of requestBodyList) {
      if (Object.keys(requestBody).length > 0) {
        try{
        const response = await downloadReport(requestBody);
        idList[response.data.id] = requestBody.attributes.name;
        }
        catch(err){
          return;
        }
      }
      if(requestBody.attributes.emailReportEnabled){
        return;
      }
    };
     addOrUpdateDownload( id, name);
     dispatch(setDownloadCount(1));
     dispatch(setShowDownload(true))
     donwloadReportWithStatus(idList, []);
  }

  // const callNewReportMethod = async (requestBodyList: downloadReportRequestBody[]) => {
  //   try {
  //     // Add download to tracking
  //     addOrUpdateDownload(id, name);
  //     dispatch(setDownloadCount(1));
  //     dispatch(setShowDownload(true));

  //     for (const requestBody of requestBodyList) {
  //       if (Object.keys(requestBody).length > 0) {
  //         // Transform the request body to new API format
  //         const reportName = requestBody.attributes.name || id;
          
  //         const getReportParams = (attributes: any, lob: string) => {
  //           const baseParams: Record<string, any> = {};
            
  //           // Get the logged-in user's login ID (distributorCodes)
  //           const loggedInUser = JSON.parse(localStorage.authContext).user.loginId;
  //           baseParams.distributorCodes = loggedInUser;
        
  //           return baseParams;
  //         };

  //         const newReportRequest: NewReportRequestBody = {
  //           configName: reportName,
  //           params: getReportParams(requestBody.attributes, requestBody.lob)
  //         };

  //         try {
  //           // Debug: Log the request being sent
  //           console.log("Report Name:", reportName);
  //           console.log("New Report Request:", newReportRequest);
  //           console.log("Original Request Body:", requestBody);
            
  //           const response = await downloadNewReport(newReportRequest);
            
  //           // Handle the response - the new API returns the file directly as a blob
  //           const blob = new Blob([response], { type: 'application/octet-stream' });
  //           const url = window.URL.createObjectURL(blob);
  //           const a = document.createElement('a');
  //           a.href = url;
  //           a.download = `${requestBody.attributes.name}_report.${requestBody.attributes.format || 'xlsx'}`;
  //           document.body.appendChild(a);
  //           a.click();
  //           document.body.removeChild(a);
  //           window.URL.revokeObjectURL(url);
            
  //         } catch (apiError) {
  //           console.error("Error calling new report API:", apiError);
  //           throw apiError;
  //         }
  //       }
  //     }

  //     // Update download status
  //     dispatch(updateDownload(id, ["SUCCESS"], 0));
  //     setLoader(false);
  //     dispatch(removeDownload(id));
  //     dispatch(setDownloadCount(-1));
      
  //   } catch (error) {
  //     console.error("Error in new report download:", error);
  //     openPopup("Error downloading new report", "Error");
  //     dispatch(updateDownload(id, ["FAILURE"], 0));
  //     setLoader(false);
  //     dispatch(setDownloadCount(-1));
  //   }
  // }

  const handleDownloadReport = async () => {
    if (filter) {
      if ((!startDateForAttribute || !endDateForAttribute)) {
        openPopup("Please select and apply filters", "Alert");
        return;
      }
    }
    if(setShowReportModal){
      setShowReportModal(false);
    }
    setLoader(true);
    let requestBodyList: downloadReportRequestBody[] = [];
    if ("innerReportsIds" in report) {
      for (const key of report?.innerReportsIds) {
        let newAttributes: downloadReportAttributes = {
          name: key,
          format: fileFormat,
          loggedInUserName: JSON.parse(localStorage.authContext).user.loginId,
        };

        if (filter) {
          if(filter==="quarterfilter"){
            // if(quarterForAttribute){
            //   newAttributes.quarter = quarterForAttribute;
            // }
            if(yearForAttribute){
              newAttributes.year = yearForAttribute;
            }
          }else{
            newAttributes.fromDate = startDateForAttribute;
            newAttributes.toDate = endDateForAttribute;
            if(filter==="periodfilter" || filter==="daterangeAndPeriod"){
              if(periodForAttribute){
                newAttributes.period = periodForAttribute;
              }
              if(yearForAttribute){
                newAttributes.year = yearForAttribute;
              }
            }
          }
        }
        if (Object.keys(reportGlobalFiltersState).length !== 0) {
          const filter = getReportFilterAttribute(reportGlobalFiltersState);
          newAttributes.filter = filter;
        }
        
        // Append _api to report name for new reports with past3months or custom filter
        // if (report?.isNewReport === true && (selectedLiveFilter === 'past3months' || selectedLiveFilter === 'custom')) {
        //   newAttributes.name = newAttributes.name + '_api';
        // }
        
        let requestBody: downloadReportRequestBody = {
          attributes: newAttributes,
          lob: localStorage.lob,
        };
        
        // if (report?.isNewReport === true ) {
        //   const loggedInUser = JSON.parse(localStorage.authContext).user.loginId;
        //   const filterObj = requestBody.attributes.filter as any;
          
         
        //   const hasFilters = filterObj && 
        //                     filterObj.filters && 
        //                     Array.isArray(filterObj.filters) && 
        //                     filterObj.filters.length > 0;
          
        //   if (hasFilters) {
        //     filterObj.filters.push({
        //       key: 'distributor_codes',
        //       value: loggedInUser
        //     });
        //   } else {
        //     // If filter object doesn't exist, create the structure
        //     requestBody.attributes.filter = {
        //       filters: [
        //         {
        //           key: 'distributor_codes',
        //           value: loggedInUser
        //         }
        //       ]
        //     };
        //   }

        // //   Add special parameter for past7days live filter
        //   // if (selectedLiveFilter === 'past7days') {
        //   //   const filterObj = requestBody.attributes.filter as any;
        //   //   if (filterObj && filterObj.filters) {
        //   //     filterObj.filters.push({
        //   //       key: 'live_filter_past7days',
        //   //       value: 'true'
        //   //     });
        //   //   }
        //   // }
        
        // }
        // else{
        //   console.log("not new report");
        // }
        
        requestBodyList.push(requestBody);
      };
    }
    else {
      let newAttributes: downloadReportAttributes = {
        name: id,
        format: fileFormat,
        loggedInUserName: JSON.parse(localStorage.authContext).user.loginId,
      };

      if (filter) {
        if(filter==="quarterfilter"){
          // if(quarterForAttribute){
          //   newAttributes.quarter = quarterForAttribute;
          // }
          if(yearForAttribute){
            newAttributes.year = yearForAttribute;
          }
        }else{
          newAttributes.fromDate = startDateForAttribute;
          newAttributes.toDate = endDateForAttribute;
          if(filter==="periodfilter" || filter==="daterangeAndPeriod"){
            if(periodForAttribute){
              newAttributes.period = periodForAttribute;
            }
            if(yearForAttribute){
              newAttributes.year = yearForAttribute;
            }
          }
        }
      }
      if (Object.keys(reportGlobalFiltersState).length !== 0) {
        const filter = getReportFilterAttribute(reportGlobalFiltersState);
        newAttributes.filter = filter;
      }
      // new report filter 
      if(selectedParentFilter && (filter=="daterangeAndPeriod" || filter == "daterange")){
        newAttributes.type=selectedParentFilter.id;
        
      }
      if(selectedParentFilter && selectedParentFilter.reportName){
        newAttributes.name = selectedParentFilter.reportName;
      }
      
      // Append _api to report name for new reports with past3months or custom filter
      // if (report?.isNewReport === true && (selectedLiveFilter === 'past3months' || selectedLiveFilter === 'custom')) {
      //   newAttributes.name = newAttributes.name + '_api';
      // }
      
      let requestBody: downloadReportRequestBody = {
        attributes: newAttributes,
        lob: localStorage.lob,
      };
      if(id==="integrationHistoryReport" && reportGlobalFiltersState.hasOwnProperty('status')){
        const statusValue = reportGlobalFiltersState['status'];
        if(statusValue==="Failure"){
          requestBody.attributes.name="integrationHistoryFailureReport"
        }else if(statusValue==="Success"){
          requestBody.attributes.name="integrationHistorySuccessReport"
        }
      }
    
      // if (report?.isNewReport === true ) {
      //     const loggedInUser = JSON.parse(localStorage.authContext).user.loginId;
      //     const filterObj = requestBody.attributes.filter as any;
          
      //    // Check if filters are present in the attributes
      //     const hasFilters = filterObj && 
      //                     filterObj.filters && 
      //                     Array.isArray(filterObj.filters) && 
      //                     filterObj.filters.length > 0;
        
      //   if (hasFilters) {
      //    // If filter is present and isNewReport == true, append distributorCodes to the filters array
      //     filterObj.filters.push({
      //       key: 'distributor_code',
      //       value: loggedInUser
      //     });
      //   } else {
      //     // If filter object doesn't exist, create the structure
      //     requestBody.attributes.filter = {
      //       filters: [
      //         {
      //           key: 'distributor_code',
      //           value: loggedInUser
      //         }
      //       ]
      //     };
      //   }

      //   // Add special parameter for past7days live filter
      //   // if (selectedLiveFilter === 'past7days') {
      //   //   const filterObj = requestBody.attributes.filter as any;
      //   //   if (filterObj && filterObj.filters) {
      //   //     filterObj.filters.push({
      //   //       key: 'live_filter_past7days',
      //   //       value: 'true'
      //   //     });
      //   //   }
      //   // }
      // }
      // else{
      //   console.log("not new report");
      // }
      
      requestBodyList.push(requestBody);
    }

    if (report.emailReportEnabled === true) {
      const userEmail = JSON.parse(localStorage.authContext).user.email;
      for (const requestBody of requestBodyList) {
        requestBody.attributes.emailReportEnabled = true;
        requestBody.attributes.emailRecipient = userEmail;
      }
      openPopup("The report will be sent to your email.","Success");
    }

    const shouldUseLiveApi =
      report?.isNewReport === true ;
      //&&
     // filter === "newFilter" ;
      //&&
      //(selectedLiveFilter === "past3months" || selectedLiveFilter === "custom");

    if (shouldUseLiveApi) {
      await downloadLiveFilterReports(requestBodyList);
      return;
    }

    // Check if this is a new report type
    // if (report.isNewReport === true) {
    //   // Use different API for new report type
    //   await callNewReportMethod(requestBodyList);
    // } else {
    //   // Use conventional method for existing reports
    //   await callMethod(requestBodyList);
    // }
    await callMethod(requestBodyList);
  }

  return (
    <>
      <GenericPopUp
        message={genericMessage}
        type={popupType}
        openGenericModal={openGenericModal}
        setOpenGenericModal={setOpenGenericModal}
      />
      {loader ? (
        <Loader className='download-report-loader' />
      ) : (

        <div className='download-page-component-box' onClick={handleDownloadReport}>
          <div className='download-icon'>
            <DownloadForOfflineIcon fontSize='inherit' />
          </div>
          <div style={{ color: "#23c6b0", fontWeight: 600 }}>{translate(TranslationEnum.common_portal,"Download Here")}</div>
        </div>
      )}
    </>
  )
}
export default DownloadPageComponent;